import { useEffect, useCallback } from 'react';
import type { Editor } from '@tiptap/react';
import api from '../../../services/api';
import {
  FILE_STORE_UPLOAD_URL,
  IMAGE_STORE_UPLOAD_URL,
  normalizeUploadContextId,
} from '../../detail-form/richTextMediaHelpers';
import { useResolvedMediaSync } from '../../detail-form/hooks/useResolvedMediaSync';

const createPreviewUrl = (file: File): string => URL.createObjectURL(file);

const revokePreviewUrl = (url?: string | null) => {
  if (!url) return;
  try {
    URL.revokeObjectURL(url);
  } catch (_err) {
    console.warn('Failed to revoke object URL', _err);
  }
};

const dataUrlToFile = (dataurl: string, filename = 'pasted-image.png') => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] ?? '';
  const bstr = atob(arr[1] || '');
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
};

const uploadFileToStore = async (file: File, uploadContextId: string): Promise<string | null> => {
  const formData = new FormData();
  formData.append('file', file);
  const uploadEndpoint = file.type.startsWith('image/')
    ? IMAGE_STORE_UPLOAD_URL
    : FILE_STORE_UPLOAD_URL;
  try {
    const resp = await api.post(uploadEndpoint, formData, {
      params: { dataUid: uploadContextId },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp?.data?.url ?? null;
  } catch (err) {
    console.error('Upload failed', err);
    return null;
  }
};

const insertPreviewImage = (editor: Editor | null, src: string) => {
  (editor as any)?.commands?.setImage?.({ src });
};

const insertFileLink = (editor: Editor | null, url: string, fileName: string) => {
  (editor as any)?.commands?.insertContent?.({
    type: 'text',
    text: fileName,
    marks: [
      {
        type: 'link',
        attrs: { href: url, target: '_blank', rel: 'noopener noreferrer' },
      },
    ],
  });
};

const replaceImageNodeBySrc = (
  editor: Editor | null,
  matchSrc: string,
  newSrc: string
): boolean => {
  if (!editor || (editor as any).isDestroyed) return false;
  const { tr } = editor.state;
  let applied = false;
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'image' && node.attrs?.src === matchSrc) {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: newSrc });
      applied = true;
    }
  });
  if (applied) {
    editor.view.dispatch(tr.setMeta('addToHistory', false));
  }
  return applied;
};

const setCaretAtCoords = (editor: Editor | null, x: number, y: number) => {
  if (!editor?.view) return;
  const posAtCoords = (editor.view as any).posAtCoords?.bind(editor.view);
  const coords = posAtCoords ? (editor.view as any).posAtCoords({ left: x, top: y }) : undefined;
  if (coords?.pos != null) {
    const setTextSelection = (editor as any)?.commands?.setTextSelection;
    if (typeof setTextSelection === 'function') {
      setTextSelection(coords.pos);
      editor.view.focus();
    } else {
      editor.commands.focus();
    }
  } else {
    editor.commands.focus();
  }
};

type UseCommentUploadsReturn = {
  uploadAndInsertFile: (file: File) => Promise<string | null>;
};

export default function useCommentUploads(
  editor: Editor | null,
  uploadContextId?: string | number
): UseCommentUploadsReturn {
  const { scheduleResolvedMediaSync } = useResolvedMediaSync({ editor });
  const uploadAndInsertFile = useCallback(
    async (file: File) => {
      if (!editor || (editor as any).isDestroyed) return null;

      const params = new URLSearchParams(window.location.search);
      const fallbackId = params.get('id') ?? undefined;
      const resolvedUploadContextId = normalizeUploadContextId(uploadContextId ?? fallbackId);
      if (!resolvedUploadContextId) {
        console.warn('useCommentUploads: uploadContextId is missing; cannot upload file');
        return null;
      }

      // Create a local preview immediately so the user sees the image without hitting AppMedia (avoids 403)
      const previewUrl = createPreviewUrl(file);
      insertPreviewImage(editor, previewUrl);
      try {
        const finalUrl = await uploadFileToStore(file, resolvedUploadContextId);
        if (!finalUrl) {
          revokePreviewUrl(previewUrl);
          return null;
        }

        // Replace preview with the server-provided path immediately.
        if (file.type.startsWith('image/')) {
          // Try to replace the preview image node; if not found, insert image
          const replaced = replaceImageNodeBySrc(editor, previewUrl, finalUrl);
          if (!replaced) insertPreviewImage(editor, finalUrl);
        } else {
          insertFileLink(editor, finalUrl, file.name);
        }

        // Ask the shared resolver hook to resolve any AppMedia paths (converts to presigned URLs)
        if (scheduleResolvedMediaSync) {
          try {
            await scheduleResolvedMediaSync(true);
          } catch (resolveErr) {
            console.warn('scheduleResolvedMediaSync failed', resolveErr);
          }
        }

        // cleanup preview URL
        revokePreviewUrl(previewUrl);

        return finalUrl;
      } catch (err) {
        console.error('Upload failed', err);
        revokePreviewUrl(previewUrl);
        return null;
      }
    },
    [editor, uploadContextId, scheduleResolvedMediaSync]
  );

  useEffect(() => {
    if (!editor?.view) return;
    const viewDom = editor.view.dom as HTMLElement;

    const handlePaste = async (event: ClipboardEvent) => {
      const clipboard = event.clipboardData;
      if (!clipboard) return;

      const files = Array.from(clipboard.files || []);
      if (files.length) {
        event.preventDefault();
        for (const file of files) {
          await uploadAndInsertFile(file);
        }
        return;
      }

      const html = clipboard.getData('text/html');
      if (html) {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const imgs = Array.from(tmp.querySelectorAll('img'));
        if (imgs.length) {
          event.preventDefault();
          for (const img of imgs) {
            const src = img.getAttribute('src') ?? '';
            if (src.startsWith('data:')) {
              const file = dataUrlToFile(src);
              await uploadAndInsertFile(file);
            }
          }
        }
      }
    };

    const handleDrop = async (event: DragEvent) => {
      const dt = event.dataTransfer;
      if (!dt) return;
      const files = Array.from(dt.files || []);
      if (!files.length) return;
      event.preventDefault();

      // Try to place the caret at drop coordinates if possible (safe helper)
      setCaretAtCoords(editor, event.clientX, event.clientY);

      for (const file of files) {
        await uploadAndInsertFile(file);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };

    const pasteListener = (e: Event) => {
      void handlePaste(e as ClipboardEvent);
    };

    const dropListener = (e: Event) => {
      void handleDrop(e as DragEvent);
    };

    const dragOverListener = (e: Event) => {
      handleDragOver(e as DragEvent);
    };

    viewDom.addEventListener('paste', pasteListener);
    viewDom.addEventListener('drop', dropListener);
    viewDom.addEventListener('dragover', dragOverListener);

    return () => {
      viewDom.removeEventListener('paste', pasteListener);
      viewDom.removeEventListener('drop', dropListener);
      viewDom.removeEventListener('dragover', dragOverListener);
    };
  }, [editor, uploadAndInsertFile]);

  return { uploadAndInsertFile };
}
